import * as React from "react";
import { TextField, Button, Typography, Box, Divider, Accordion, AccordionSummary, AccordionDetails, Grid, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PreProdDeploy } from "./PreProdDeploy";

interface ServerInfo {
    game?: string;
    chat?: string;
    files?: string;
    filesFallback?: string;
    assets?: string;
    assetsFallback?: string;
    versionConfigs?: Array<{
        minVer: string;
        maxVer: string;
        game?: string;
        chat?: string;
        files?: string;
        filesFallback?: string;
        assets?: string;
        assetsFallback?: string;
    }>;
}

interface ServerOverrideInfo {
    serverConfig: ServerInfo;
    whiteList?: string[];
}

interface DeployInfo {
    domains: string[];
    currentServer: string | null;
    targetServer: string | null;
    buildImage: {
        imageDigest: string;
        imageTag: string;
    };
}

interface DeploymentProps {
    activeEnv: string;
}

export class ECSDeployment extends React.Component<any, { serverConfig: ServerInfo; serverOverrideConfig: ServerOverrideInfo, deployConfig: DeployInfo }> {
    private isMountedFlag: boolean = false;
    private preProdRef = React.createRef<PreProdDeploy>();
    constructor(props: any) {
        super(props);
        if(props.socket){
            console.log("Socket is connected");
        }
        // Initialize state with grouped serverInfo and serverOverrideInfo
        this.state = {
            serverConfig: {
                game: "",
                chat: "",
                files: "",
                filesFallback: "",
                assets: "",
                assetsFallback: "",
                versionConfigs: [],
            },
            serverOverrideConfig: {
                serverConfig: {
                    game: "",
                    chat: "",
                    files: "",
                    filesFallback: "",
                    assets: "",
                    assetsFallback: "",
                    versionConfigs: [],
                },
                whiteList: [],
            },
            deployConfig: {
                domains: [],
                currentServer: null,
                targetServer: null,
                buildImage: { imageDigest: "", imageTag: "" },
            },
        };
    }

    componentDidMount() {
        this.isMountedFlag = true;
        this.fetchServerConfig();
        //this.fetchDeploymentConfig();
    }

    componentWillUnmount() {
        this.isMountedFlag = false;
    }

    fetchServerConfig = async () => {
        try {
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/server-config/auth-config`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                },
            });

            const data = response.data;

            if (this.isMountedFlag) {
                // Preserve all existing data from the server, only update fields that are defined
                const currentServerConfig = this.state.serverConfig;
                const currentServerOverrideConfig = this.state.serverOverrideConfig;
                
                this.setState({
                    serverConfig: {
                        ...currentServerConfig, // Preserve any existing fields not in the interface
                        game: data.serverConfig?.game || currentServerConfig.game || "",
                        chat: data.serverConfig?.chat || currentServerConfig.chat || "",
                        files: data.serverConfig?.files || currentServerConfig.files || "",
                        filesFallback: data.serverConfig?.filesFallback || currentServerConfig.filesFallback || "",
                        assets: data.serverConfig?.assets || currentServerConfig.assets || "",
                        assetsFallback: data.serverConfig?.assetsFallback || currentServerConfig.assetsFallback || "",
                        versionConfigs: data.serverConfig?.versionConfigs || currentServerConfig.versionConfigs || [],
                    },
                    serverOverrideConfig: {
                        ...currentServerOverrideConfig, // Preserve any existing fields not in the interface
                        serverConfig: {
                            ...currentServerOverrideConfig.serverConfig, // Preserve any existing fields not in the interface
                            game: data.serverOverrideConfig?.serverConfig?.game || currentServerOverrideConfig.serverConfig.game || "",
                            chat: data.serverOverrideConfig?.serverConfig?.chat || currentServerOverrideConfig.serverConfig.chat || "",
                            files: data.serverOverrideConfig?.serverConfig?.files || currentServerOverrideConfig.serverConfig.files || "",
                            filesFallback: data.serverOverrideConfig?.serverConfig?.filesFallback || currentServerOverrideConfig.serverConfig.filesFallback || "",
                            assets: data.serverOverrideConfig?.serverConfig?.assets || currentServerOverrideConfig.serverConfig.assets || "",
                            assetsFallback: data.serverOverrideConfig?.serverConfig?.assetsFallback || currentServerOverrideConfig.serverConfig.assetsFallback || "",
                            versionConfigs: data.serverOverrideConfig?.serverConfig?.versionConfigs || currentServerOverrideConfig.serverConfig.versionConfigs || [],
                        },
                        whiteList: data.serverOverrideConfig?.whiteList || currentServerOverrideConfig.whiteList || [],
                    },
                });
            }
        } catch (error: any) {
            if (this.isMountedFlag) {
                alert(`Error fetching server config: ${error.response?.data?.message || error.message}`);
            }
        }
    };

    fetchDeploymentConfig = async () => {
        try {
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/server-config/deploy-config`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                },
            });

            const data = response.data;
            console.log("Deployment Config:", data);
        } catch (error: any) {
            if (this.isMountedFlag) {
                alert(`Error fetching server config: ${error.response?.data?.message || error.message}`);
            }
        }
    }

    handleServerInfoChange = (field: keyof ServerInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            serverConfig: {
                ...this.state.serverConfig,
                [field]: event.target.value,
            },
        });
    };

    handleServerOverrideChange = (field: keyof ServerInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            serverOverrideConfig: {
                ...this.state.serverOverrideConfig,
                serverConfig: {
                    ...this.state.serverOverrideConfig.serverConfig,
                    [field]: event.target.value,
                },
            },
        });
    };

    handleWhiteListChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const whiteList = event.target.value.split(",").map((item) => item.trim());
        this.setState({
            serverOverrideConfig: {
                ...this.state.serverOverrideConfig,
                whiteList,
            },
        });
    };

    handleVersionConfigChange = (configType: 'serverConfig' | 'serverOverrideConfig', index: number, field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (configType === 'serverConfig') {
            const configs = [...(this.state.serverConfig.versionConfigs || [])];
            if (!configs[index]) {
                configs[index] = {
                    minVer: "",
                    maxVer: "",
                    game: "",
                    chat: "",
                    files: "",
                    filesFallback: "",
                    assets: "",
                    assetsFallback: "",
                };
            }
            configs[index] = { ...configs[index], [field]: event.target.value };
            
            this.setState({
                serverConfig: {
                    ...this.state.serverConfig,
                    versionConfigs: configs,
                },
            });
        } else {
            const configs = [...(this.state.serverOverrideConfig.serverConfig.versionConfigs || [])];
            if (!configs[index]) {
                configs[index] = {
                    minVer: "",
                    maxVer: "",
                    game: "",
                    chat: "",
                    files: "",
                    filesFallback: "",
                    assets: "",
                    assetsFallback: "",
                };
            }
            configs[index] = { ...configs[index], [field]: event.target.value };
            
            this.setState({
                serverOverrideConfig: {
                    ...this.state.serverOverrideConfig,
                    serverConfig: {
                        ...this.state.serverOverrideConfig.serverConfig,
                        versionConfigs: configs,
                    },
                },
            });
        }
    };

    addVersionConfig = (configType: 'serverConfig' | 'serverOverrideConfig') => {
        const newConfig = {
            minVer: "",
            maxVer: "",
            game: "",
            chat: "",
            files: "",
            filesFallback: "",
            assets: "",
            assetsFallback: "",
        };
        
        if (configType === 'serverConfig') {
            this.setState({
                serverConfig: {
                    ...this.state.serverConfig,
                    versionConfigs: [...(this.state.serverConfig.versionConfigs || []), newConfig],
                },
            });
        } else {
            this.setState({
                serverOverrideConfig: {
                    ...this.state.serverOverrideConfig,
                    serverConfig: {
                        ...this.state.serverOverrideConfig.serverConfig,
                        versionConfigs: [...(this.state.serverOverrideConfig.serverConfig.versionConfigs || []), newConfig],
                    },
                },
            });
        }
    };

    removeVersionConfig = (configType: 'serverConfig' | 'serverOverrideConfig', index: number) => {
        if (configType === 'serverConfig') {
            const configs = [...(this.state.serverConfig.versionConfigs || [])];
            configs.splice(index, 1);
            
            this.setState({
                serverConfig: {
                    ...this.state.serverConfig,
                    versionConfigs: configs,
                },
            });
        } else {
            const configs = [...(this.state.serverOverrideConfig.serverConfig.versionConfigs || [])];
            configs.splice(index, 1);
            
            this.setState({
                serverOverrideConfig: {
                    ...this.state.serverOverrideConfig,
                    serverConfig: {
                        ...this.state.serverOverrideConfig.serverConfig,
                        versionConfigs: configs,
                    },
                },
            });
        }
    };

    handleSubmit = async () => {
        try {
            const response = await axios.post(`${Config.getInstance.apiUrl}/api/server-config/auth-config`, {
                serverConfig: this.state.serverConfig,
                serverOverrideConfig: this.state.serverOverrideConfig,
            },
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    },
                });
            if (response.status === 200) {
                alert("All configurations submitted successfully!");
                this.preProdRef.current?.fetchDeployInfo();
            } else {
                alert("Failed to submit configurations.");
            }
        } catch (error: any) {
            alert(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    handleTriggerActiveServer = (server: string) => {
        const currentServer = this.state.serverConfig?.game;
        const protocol = currentServer && currentServer.includes('://') ? currentServer.split('://')[0] : '';
        if(protocol) {
            server = `${protocol}://${server}`;
        }        
        console.log("Triggering active server", server);
        this.setState({
            serverConfig: {
                ...this.state.serverConfig,
                game: server,
            },
        }, () => {
            // call save server config with updated state
            this.handleSubmit();
        });
    };

    render() {
        const { serverConfig: serverInfo, serverOverrideConfig: serverOverrideInfo } = this.state;

        return (
            <Box sx={{ padding: "20px", maxWidth: "80%", margin: "0 auto" }}>
                <Typography variant="h4" gutterBottom>
                    ECS Deployment
                </Typography>
                <Box sx={{ padding: "20px", maxWidth: "100%", margin: "0 auto" }}>
                    <Accordion elevation={4}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h5">Server Info</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" gutterBottom>
                                This setting is used to override the environment variables. If omitted, it will use the default value of the environment variables unless the device ID is in the whitelist of the override configuration.
                            </Typography>
                            <TextField
                                label="Game Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.game}
                                onChange={this.handleServerInfoChange("game")}
                            />
                            <TextField
                                label="Chat Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.chat}
                                onChange={this.handleServerInfoChange("chat")}
                            />
                            <TextField
                                label="Files Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.files}
                                onChange={this.handleServerInfoChange("files")}
                            />
                            <TextField
                                label="Files Fallback Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.filesFallback}
                                onChange={this.handleServerInfoChange("filesFallback")}
                            />
                            <TextField
                                label="Assets Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.assets}
                                onChange={this.handleServerInfoChange("assets")}
                            />
                            <TextField
                                label="Assets Fallback Server"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverInfo.assetsFallback}
                                onChange={this.handleServerInfoChange("assetsFallback")}
                            />
                            
                            {/* Version Configs Section */}
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Version-Based Configurations
                                    </Typography>
                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => this.addVersionConfig('serverConfig')}
                                        variant="outlined"
                                        size="small"
                                    >
                                        Add Version Config
                                    </Button>
                                </Box>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Configure different server settings for specific app versions. Version ranges should not overlap.
                                </Typography>
                                
                                {serverInfo.versionConfigs?.map((config, index) => (
                                    <Box key={index} sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1">Version Config {index + 1}</Typography>
                                            <IconButton
                                                onClick={() => this.removeVersionConfig('serverConfig', index)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Min Version (x.y.z)"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.minVer || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'minVer')}
                                                    placeholder="1.0.0"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Max Version (x.y.z)"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.maxVer || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'maxVer')}
                                                    placeholder="1.9.9"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Game Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.game || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'game')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Chat Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.chat || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'chat')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Files Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.files || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'files')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Files Fallback Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.filesFallback || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'filesFallback')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Assets Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.assets || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'assets')}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Assets Fallback Server"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    value={config.assetsFallback || ""}
                                                    onChange={this.handleVersionConfigChange('serverConfig', index, 'assetsFallback')}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion elevation={4}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h5">Server Override Info</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" gutterBottom>
                                This setting is used to override the configuration for users in the whitelist. The priority is: Server Override Setting {">"} Server Config {">"}  Default Environment Variables.
                            </Typography>
                            <TextField
                                label="Whitelist (comma-separated)"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.whiteList?.join(", ") || ""}
                                onChange={this.handleWhiteListChange}
                            />
                            <TextField
                                label="Game Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.game}
                                onChange={this.handleServerOverrideChange("game")}
                            />
                            <TextField
                                label="Chat Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.chat}
                                onChange={this.handleServerOverrideChange("chat")}
                            />
                            <TextField
                                label="Files Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.files}
                                onChange={this.handleServerOverrideChange("files")}
                            />
                            <TextField
                                label="Files Fallback Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.filesFallback}
                                onChange={this.handleServerOverrideChange("filesFallback")}
                            />
                            <TextField
                                label="Assets Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.assets}
                                onChange={this.handleServerOverrideChange("assets")}
                            />
                            <TextField
                                label="Assets Fallback Server Override"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={serverOverrideInfo.serverConfig.assetsFallback}
                                onChange={this.handleServerOverrideChange("assetsFallback")}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Box>
                <Grid container spacing={2}>
                    {/* Server Info Section */}
                    <Grid item xs={12} md={6}>

                    </Grid>

                    {/* Server Override Info Section */}
                    <Grid item xs={12} md={6}>

                    </Grid>
                </Grid>

                {/* Submit Button */}
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px", marginBottom: "40px" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ maxWidth: "300px" }}
                        onClick={this.handleSubmit}
                    >
                        SAVE
                    </Button>
                </Box>

                {/* Pre-Prod Deployment Section - Full Width */}
                <Box sx={{ width: "100%", marginTop: "20px" }}>                   
                    <PreProdDeploy activeEnv={this.props.activeEnv} ref={this.preProdRef} onTriggerActiveServer={this.handleTriggerActiveServer} socket={this.props.socket}/>
                </Box>
            </Box>
        );
    }
}